import React, { useState, useEffect } from "react"
import { navigate } from "vike/client/router"
import Typography from "aldoo-ra/Typography"
import Value from "aldoo-ra/Value"
import { showNotification } from "aldoo-ra/Notification"
import PermissionCheck, { usePermissionCheck } from "aldoo-ra/PermissionChecker"
import {
  ChartBarIcon,
  UserGroupIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  ChartPieIcon,
} from "@heroicons/react/24/outline"
import NewsletterAPI from "../api"

export default function NewsletterOverview() {
  const [statistics, setStatistics] = Value("newsletter-statistics", null)
  const [loading, setLoading] = useState(true)
  const handlePermissionError = usePermissionCheck('role:newsletter')

  // Base URL for navigation
  const baseUrl = "/admin/newsletter"

  useEffect(() => {
    loadStatistics()
  }, [])

  // Navigate to filtered views
  const navigateToSubscribers = (filterParams) => {
    if (typeof window !== "undefined") {
      const queryString = filterParams ? new URLSearchParams(filterParams).toString() : ""
      const url = queryString
        ? `${baseUrl}/subscribers?${queryString}`
        : `${baseUrl}/subscribers`
      navigate(url)
    }
  }

  const navigateToReleases = (filterParams) => {
    if (typeof window !== "undefined") {
      const queryString = filterParams ? new URLSearchParams(filterParams).toString() : ""
      const url = queryString
        ? `${baseUrl}/releases?${queryString}`
        : `${baseUrl}/releases`
      navigate(url)
    }
  }

  // Load statistics
  const loadStatistics = async () => {
    try {
      setLoading(true)

      const response = await NewsletterAPI.getOverview()

      // Check for permission errors first
      if (handlePermissionError && handlePermissionError(response)) {
        return
      }

      if (response && typeof response === 'object') {
        setStatistics(response)
      } else {
        throw new Error('Invalid response format')
      }
    } catch (error) {
      console.error('Failed to load newsletter statistics:', error)
      showNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load newsletter statistics. Please try again.',
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2">
          <ArrowPathIcon className="w-5 h-5 animate-spin" />

          <Typography variant="admin_body1_dim">
            Loading newsletter statistics...
          </Typography>
        </div>
      </div>
    )
  }

  // Statistics cards data
  const statisticsCards = [
    {
      title: "Total Subscribers",
      value: statistics?.subscribers?.total || 0,
      subtitle: `${statistics?.subscribers?.active || 0} active, ${statistics?.subscribers?.inactive || 0} unsubscribed`,
      icon: UserGroupIcon,
      color: "blue",
      onClick: () => navigateToSubscribers(),
    },
    {
      title: "Total Releases",
      value: statistics?.releases?.total || 0,
      subtitle: `${statistics?.releases?.running || 0} running, ${statistics?.releases?.scheduled || 0} scheduled`,
      icon: EnvelopeIcon,
      color: "green",
      onClick: () => navigateToReleases(),
    },
    {
      title: "Emails Sent Today",
      value: statistics?.sending?.today?.sent || 0,
      subtitle: `${statistics?.sending?.today?.successRate || 100}% success rate`,
      icon: ChartBarIcon,
      color: "purple",
      onClick: () => {},
    },
    {
      title: "Unsubscribe Rate",
      value: `${statistics?.unsubscribeStats?.rate || 0}%`,
      subtitle: `${statistics?.unsubscribeStats?.thisMonth || 0} this month`,
      icon: ChartPieIcon,
      color: "orange",
      onClick: () => navigateToSubscribers({ filter: 'unsubscribed' }),
    },
  ]

  return (
    <div className="p-4">
      <PermissionCheck permission="role:newsletter" />
      <div className="space-y-6">
        {/* Header with Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="space-y-2">
            <Typography 
              component="h1"
              variant="admin_h3" 
              className="text-admin_text mt-2"
            >
              Newsletter Overview
            </Typography>

            <Typography 
              variant="admin_body1_dim"
              className="mt-2 mb-0"
            >
              Manage subscribers, releases, and track email campaign performance
            </Typography>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 xl:gap-6">
          {statisticsCards.map((card, index) => {
            const IconComponent = card.icon
            return (
              <div
                key={index}
                className="h-[194px] flex flex-col items-start justify-between gap-6 bg-admin_paper rounded-xl border border-transparent hover:border-admin_primary/30 hover:shadow-md p-6 transition-all duration-600 cursor-pointer group"
                onClick={card.onClick}
              >
                {/* Texts */}
                <div>
                  <Typography 
                    variant="admin_h4" 
                    className="font-semibold"
                  >
                    {card.title}
                  </Typography>

                  <Typography 
                    variant="admin_txt2_dim"
                  >
                    {card.subtitle}
                  </Typography>
                </div>

                {/* Stats */}
                <div className="w-full flex items-end justify-between mt-auto">
                  <div className="bg-admin_canvas rounded-xl p-3">
                    <IconComponent className={`w-6 h-6 text-${card.color}-600`} />
                  </div>

                  <Typography 
                    component="output"
                    variant="admin_h2" 
                    className={`text-${card.color}-600`}
                  >
                    {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                  </Typography>
                </div>
              </div>
            )
          })}
        </div>

        {/* Mail Service Usage */}
        {statistics?.mailServiceUsage && (
          <div className="bg-paper rounded-xl shadow-sm border border-admin_bordered">
            <div className="border-b border-admin_bordered p-6">
              <Typography
                component="h2"
                variant="admin_h4"
                className="mb-1"
              >
                Mail Service Usage
              </Typography>

              <Typography variant="admin_txt1_dim">
                SparkPost account limits and consumption
              </Typography>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Daily Usage */}
                <div className="border border-admin_bordered rounded-lg p-4">
                  <Typography
                    variant="admin_txt2_dim"
                    className="mb-3"
                  >
                    Daily Usage
                  </Typography>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Typography variant="admin_txt1">
                        Limit:
                      </Typography>
                      <Typography
                        variant="admin_txt1"
                        className="font-semibold"
                      >
                        {statistics.mailServiceUsage.daily.limit.toLocaleString()}
                      </Typography>
                    </div>

                    <div className="flex items-center justify-between">
                      <Typography variant="admin_txt1">
                        Used:
                      </Typography>
                      <Typography
                        variant="admin_txt1"
                        className="font-semibold text-admin_warning"
                      >
                        {statistics.mailServiceUsage.daily.used.toLocaleString()}
                      </Typography>
                    </div>

                    <div className="flex items-center justify-between">
                      <Typography variant="admin_txt1">
                        Remaining:
                      </Typography>
                      <Typography
                        variant="admin_txt1"
                        className="font-semibold text-admin_success"
                      >
                        {statistics.mailServiceUsage.daily.remaining.toLocaleString()}
                      </Typography>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <Typography variant="admin_txt2_dim">
                          Usage
                        </Typography>
                        <Typography variant="admin_txt2_dim">
                          {statistics.mailServiceUsage.daily.percentUsed.toFixed(1)}%
                        </Typography>
                      </div>
                      <div className="w-full bg-admin_canvas rounded-full h-2">
                        <div
                          className="bg-admin_warning rounded-full h-2 transition-all duration-300"
                          style={{ width: `${Math.min(statistics.mailServiceUsage.daily.percentUsed, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Monthly Usage */}
                <div className="border border-admin_bordered rounded-lg p-4">
                  <Typography
                    variant="admin_txt2_dim"
                    className="mb-3"
                  >
                    Monthly Usage
                  </Typography>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Typography variant="admin_txt1">
                        Limit:
                      </Typography>
                      <Typography
                        variant="admin_txt1"
                        className="font-semibold"
                      >
                        {statistics.mailServiceUsage.monthly.limit.toLocaleString()}
                      </Typography>
                    </div>

                    <div className="flex items-center justify-between">
                      <Typography variant="admin_txt1">
                        Used:
                      </Typography>
                      <Typography
                        variant="admin_txt1"
                        className="font-semibold text-admin_warning"
                      >
                        {statistics.mailServiceUsage.monthly.used.toLocaleString()}
                      </Typography>
                    </div>

                    <div className="flex items-center justify-between">
                      <Typography variant="admin_txt1">
                        Remaining:
                      </Typography>
                      <Typography
                        variant="admin_txt1"
                        className="font-semibold text-admin_success"
                      >
                        {statistics.mailServiceUsage.monthly.remaining.toLocaleString()}
                      </Typography>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <Typography variant="admin_txt2_dim">
                          Usage
                        </Typography>
                        <Typography variant="admin_txt2_dim">
                          {statistics.mailServiceUsage.monthly.percentUsed.toFixed(1)}%
                        </Typography>
                      </div>
                      <div className="w-full bg-admin_canvas rounded-full h-2">
                        <div
                          className="bg-admin_warning rounded-full h-2 transition-all duration-300"
                          style={{ width: `${Math.min(statistics.mailServiceUsage.monthly.percentUsed, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Source */}
              {statistics.mailServiceUsage.source && (
                <div className="mt-4">
                  <Typography variant="admin_txt2_dim" className="text-xs">
                    Data source: {statistics.mailServiceUsage.source}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Activity & Unsubscribe Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Completed Releases */}
          {statistics?.recentActivity && statistics.recentActivity.length > 0 && (
            <div className="bg-paper border border-admin_bordered rounded-xl shadow-sm">
              <div className="border-b border-admin_bordered p-6">
                <Typography 
                  variant="admin_h3" 
                  className="text-admin_text mb-1"
                >
                  Recent Completed Releases
                </Typography>

                <Typography variant="admin_txt2_dim">
                  Latest completed newsletter campaigns
                </Typography>
              </div>

              <div className="p-6 space-y-4">
                {statistics.recentActivity.map((release, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-admin_border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Typography 
                          variant="admin_txt1" 
                          className="font-semibold text-admin_text"
                        >
                          {release.name}
                        </Typography>

                        <div className="text-xs text-admin_success bg-admin_success/10 rounded-full px-2 py-1">
                          ✅ {release.successRate}%
                        </div>
                      </div>

                      <div className="flex items-center space-x-6 text-sm">
                        <span className="text-admin_text_secondary">
                          {release.emailsSent.toLocaleString()} sent • {release.timeAgo}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unsubscribe Statistics */}
          {statistics?.unsubscribeStats && (
            <div className="bg-paper rounded-xl shadow-sm">
              <div className="border-b border-admin_bordered p-6">
                {/* Header */}
                <Typography 
                  component="h2"
                  variant="admin_h4" 
                  className="mb-1"
                >
                  Unsubscribe Analytics
                </Typography>

                <Typography variant="admin_txt1_dim">
                  Top reasons and trends
                </Typography>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <Typography 
                      variant="admin_txt1" 
                      className="font-semibold text-admin_text"
                    >
                      This Month: {statistics.unsubscribeStats.thisMonth}
                    </Typography>

                    <Typography variant="admin_txt1_dim">
                      Rate: {statistics.unsubscribeStats.rate}%
                    </Typography>
                  </div>
                </div>
                
                {statistics.unsubscribeStats.topReasons && statistics.unsubscribeStats.topReasons.length > 0 && (
                  <div>
                    <Typography 
                      variant="admin_txt1" 
                      className="font-semibold mb-3"
                    >
                      Top Reasons
                    </Typography>

                    <div className="space-y-2">
                      {statistics.unsubscribeStats.topReasons.slice(0, 3).map((reason, index) => (
                        <div key={index} className="flex items-center justify-between rounded bg-admin_canvas p-2">
                          <Typography 
                            variant="admin_txt2" 
                            className="text-admin_text"
                          >
                            {reason.reason}
                          </Typography>

                          <div className="flex items-center space-x-2">
                            <Typography variant="admin_txt2_dim">
                              {reason.count}
                            </Typography>

                            <Typography variant="admin_txt2_dim">
                              ({reason.percentage.toFixed(1)}%)
                            </Typography>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}